import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import AuthShell, { GoogleButton } from "@/components/AuthShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

const Login = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "admin" ? "/admin" : user.onboarded ? "/dashboard" : "/onboarding", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      navigate(u.role === "admin" ? "/admin" : u.onboarded ? "/dashboard" : "/onboarding", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Log in" subtitle="Access your growth dashboard."
      footer={<>Don't have an account? <Link to="/register" className="text-primary font-semibold hover:underline">Sign up</Link></>}>
      <GoogleButton />
      <div className="flex items-center gap-4 my-6">
        <div className="flex-1 h-px bg-white/10" /><span className="text-xs text-muted-foreground">or</span><div className="flex-1 h-px bg-white/10" />
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email" className="text-sm">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" placeholder="you@example.com" data-testid="login-email-input" />
        </div>
        <div>
          <Label htmlFor="password" className="text-sm">Password</Label>
          <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="mt-1.5 h-11 bg-transparent border-white/10 focus:border-primary" placeholder="••••••••" data-testid="login-password-input" />
        </div>
        <Button type="submit" disabled={loading} className="w-full h-11 rounded-full bg-primary text-background font-bold hover:bg-primary/90 transition-colors" data-testid="login-submit-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Log in"}
        </Button>
      </form>
    </AuthShell>
  );
};

export default Login;
